import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { MapPin, Loader2, Search, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AddressAutocomplete = ({ 
  onAddressSelect, 
  getAuthHeaders, 
  placeholder = "Start typing an address...",
  initialValue = "",
  className = ""
}) => {
  const [searchTerm, setSearchTerm] = useState(initialValue);
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [error, setError] = useState(null);
  const [isManualMode, setIsManualMode] = useState(false);
  
  const debounceRef = useRef(null);
  const dropdownRef = useRef(null);
  const inputRef = useRef(null);

  // Debounced search function
  useEffect(() => {
    if (!searchTerm.trim() || isManualMode || searchTerm.length < 6) { // Increased from 4 to 6 chars
      setSuggestions([]);
      setShowDropdown(false);
      return;
    }

    // Clear previous timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Set new timeout - increased delay to reduce interruptions
    debounceRef.current = setTimeout(() => {
      searchAddresses(searchTerm);
    }, 500); // Reduced from 600ms to 500ms for better responsiveness

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [searchTerm, isManualMode]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
          inputRef.current && !inputRef.current.contains(event.target)) {
        setShowDropdown(false);
        setSelectedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const searchAddresses = async (term) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.get(
        `${API}/address/autocomplete/${encodeURIComponent(term)}`,
        {
          ...getAuthHeaders(),
          params: {
            top: 6, // Maximum allowed by getaddress.io API
            all: true, // Returns all suggestions when search contains postcode
            'show-postcode': true, // Always show postcode in suggestions for clarity
            template: '{formatted_address}, {postcode}' // Include postcode in display for better identification
          }
        }
      );

      if (response.data && response.data.suggestions) {
        setSuggestions(response.data.suggestions);
        setShowDropdown(true);
        setSelectedIndex(-1);
      } else {
        setSuggestions([]);
        setShowDropdown(false);
      }
    } catch (error) {
      console.error('Address search failed:', error);
      setError(error.response?.data?.detail || 'Address search failed');
      setSuggestions([]);
      setShowDropdown(false);
      
      // Show user-friendly error message
      if (error.response?.status === 429) {
        toast.error('Address lookup rate limited. Please try again in a moment.');
      } else {
        toast.error('Address lookup unavailable. Please enter address manually.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const selectAddress = async (suggestion) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.get(
        `${API}/address/get/${suggestion.id}`,
        getAuthHeaders()
      );

      if (response.data) {
        const addressData = response.data;
        
        // Format the address data for the parent component
        const formattedAddress = {
          line_1: addressData.line_1 || '',
          line_2: addressData.line_2 || '',
          line_3: addressData.line_3 || '',
          line_4: addressData.line_4 || '',
          town_or_city: addressData.town_or_city || '',
          county: addressData.county || '',
          postcode: addressData.postcode || '',
          country: addressData.country || 'England',
          // Additional fields that might contain area information
          locality: addressData.locality || '',
          district: addressData.district || '',
          formatted_address: addressData.formatted_address || [],
          // Additional useful data
          latitude: addressData.latitude,
          longitude: addressData.longitude,
          thoroughfare: addressData.thoroughfare,
          building_name: addressData.building_name,
          building_number: addressData.building_number,
          residential: addressData.residential
        };

        setSearchTerm(suggestion.address);
        setShowDropdown(false);
        setSuggestions([]);
        setSelectedIndex(-1);
        
        // Notify parent component
        onAddressSelect(formattedAddress);
        
        toast.success('Address selected successfully');
      }
    } catch (error) {
      console.error('Failed to get full address:', error);
      setError(error.response?.data?.detail || 'Failed to get address details');
      toast.error('Failed to get address details. Please try again or enter manually.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (!showDropdown || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => {
          const nextIndex = prev < suggestions.length - 1 ? prev + 1 : 0;
          // Scroll selected item into view
          setTimeout(() => {
            const dropdown = dropdownRef.current;
            const selectedElement = dropdown?.children[0]?.children[nextIndex];
            if (selectedElement && dropdown) {
              selectedElement.scrollIntoView({ block: 'nearest' });
            }
          }, 0);
          return nextIndex;
        });
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => {
          const nextIndex = prev > 0 ? prev - 1 : suggestions.length - 1;
          // Scroll selected item into view
          setTimeout(() => {
            const dropdown = dropdownRef.current;
            const selectedElement = dropdown?.children[0]?.children[nextIndex];
            if (selectedElement && dropdown) {
              selectedElement.scrollIntoView({ block: 'nearest' });
            }
          }, 0);
          return nextIndex;
        });
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          selectAddress(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
    }
  };

  const toggleManualMode = () => {
    setIsManualMode(!isManualMode);
    if (!isManualMode) {
      // Switching to manual mode
      setShowDropdown(false);
      setSuggestions([]);
      setSearchTerm('');
      toast.info('Manual address entry enabled');
    } else {
      // Switching to autocomplete mode
      toast.info('Address autocomplete enabled');
    }
  };

  return (
    <div className="relative">
      <div className="flex gap-2">
        <div className="relative flex-1">
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400">
            {isLoading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <MapPin className="w-4 h-4" />
            )}
          </div>
          
          <Input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={isManualMode ? "Enter address manually" : placeholder}
            className={`pl-10 ${className}`}
            disabled={isLoading}
          />
          
          {error && !isLoading && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-red-400">
              <AlertCircle className="w-4 h-4" />
            </div>
          )}
        </div>
        
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={toggleManualMode}
          className="gap-2 flex-shrink-0"
          title={isManualMode ? "Switch to address lookup" : "Switch to manual entry"}
        >
          {isManualMode ? (
            <>
              <Search className="w-4 h-4" />
              Lookup
            </>
          ) : (
            "Manual"
          )}
        </Button>
      </div>

      {/* Dropdown suggestions */}
      {showDropdown && suggestions.length > 0 && !isManualMode && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto"
          style={{ 
            scrollbarWidth: 'thin',
            scrollbarColor: '#CBD5E0 #F7FAFC'
          }}
        >
          <div className="py-1">
            {suggestions.map((suggestion, index) => (
              <div
                key={suggestion.id}
                className={`px-3 py-2 cursor-pointer text-sm hover:bg-blue-50 border-l-4 border-transparent ${
                  index === selectedIndex ? 'bg-blue-50 border-l-blue-500' : 'hover:border-l-blue-200'
                }`}
                onClick={() => selectAddress(suggestion)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <div className="flex items-start gap-2">
                  <MapPin className="w-3 h-3 text-gray-400 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-900 leading-tight">{suggestion.address}</span>
                </div>
              </div>
            ))}
          </div>
          
          {/* Scroll hint at bottom if there are many results */}
          {suggestions.length >= 6 && (
            <div className="px-3 py-2 text-xs text-gray-500 bg-blue-50 border-t text-center border-blue-100">
              <div className="flex items-center justify-center gap-1">
                <span>⚡</span>
                <span>Showing top 6 matches. For more specific results, try adding area/town name.</span>
              </div>
            </div>
          )}
        </div>
      )}

      {/* No results message */}
      {showDropdown && suggestions.length === 0 && !isLoading && searchTerm.length >= 6 && !isManualMode && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-3">
          <div className="text-sm text-gray-500 text-center">
            <div className="mb-1">No addresses found for "{searchTerm}"</div>
            <div className="text-xs">Try adding the area, town, or postcode for better results</div>
          </div>
        </div>
      )}

      {/* Helper text */}
      <div className="mt-1 text-xs text-gray-500">
        {isManualMode ? (
          "Manual entry mode - type your address directly"
        ) : isLoading ? (
          "Searching for addresses..."
        ) : searchTerm.length < 6 && searchTerm.length > 0 ? (
          `Type ${6 - searchTerm.length} more character${6 - searchTerm.length === 1 ? '' : 's'} to search UK addresses`
        ) : showDropdown && suggestions.length > 0 ? (
          "📍 If your exact address isn't shown, try adding the area/town name for more precise results"
        ) : (
          "Type at least 6 characters to search UK addresses. Include area/town name for better results."
        )}
      </div>
    </div>
  );
};

export default AddressAutocomplete;